import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, SUPPORTED_LOCALES, type Locale, type User } from "@agentsaas/protocol";
import type { CallInput } from "@agentsaas/client-sdk";
import { useServices } from "./services";
import { useT } from "./i18n";
import { setThemePref } from "./theme";
import { pushApiFor } from "./native";

type SessionState =
  | { status: "loading"; user: null }
  | { status: "anon"; user: null }
  | { status: "authed"; user: User };

interface SessionApi {
  state: SessionState;
  login(body: CallInput<"authLogin">["body"]): Promise<User>;
  register(body: CallInput<"authRegister">["body"]): Promise<User>;
  logout(): Promise<void>;
  updateMe(patch: CallInput<"mePatch">["body"]): Promise<void>;
}

const Ctx = createContext<SessionApi | null>(null);
export const ME_KEY = ["me"] as const;

export function SessionProvider({ children }: { children: ReactNode }) {
  const { api, native } = useServices();
  const qc = useQueryClient();
  const { i18n } = useT();

  const me = useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await api.call("meGet");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: Infinity,
  });

  const user = me.data ?? null;
  // 登录后以用户设置为准（语言、主题）；例外：登录前在界面上手动选过语言，则把它写回用户设置
  const syncedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!user || syncedFor.current === user.id) return;
    syncedFor.current = user.id;
    const explicit = readExplicitLocale();
    if (explicit && explicit === i18n.language && explicit !== user.locale) {
      clearExplicitLocale();
      qc.setQueryData(ME_KEY, { ...user, locale: explicit });
      void api.call("mePatch", { body: { locale: explicit } }).catch(() => {});
    } else if (user.locale !== i18n.language) void i18n.changeLanguage(user.locale);
    setThemePref(user.theme);
    // 原生壳：登录态确定后向 APNs / FCM 注册本机（拒绝权限 / 失败时桥返回 null，不抛错）
    if (native) void native.push.register(pushApiFor(api), user.locale).catch(() => null);
  }, [user, i18n, api, qc, native]);

  const apply = useCallback(
    (u: User) => {
      qc.setQueryData(ME_KEY, u);
    },
    [qc],
  );

  const value = useMemo<SessionApi>(
    () => ({
      state: me.isPending ? { status: "loading", user: null } : user ? { status: "authed", user } : { status: "anon", user: null },
      async login(body) {
        const s = await api.call("authLogin", { body: native ? { ...body, mode: "token" } : body });
        apply(s.user);
        return s.user;
      },
      async register(body) {
        const s = await api.call("authRegister", { body: native ? { ...body, mode: "token" } : body });
        apply(s.user);
        return s.user;
      },
      async logout() {
        syncedFor.current = null;
        try {
          // 原生壳：趁还有登录态先注销本机推送设备
          if (native) await native.push.unregister(pushApiFor(api)).catch(() => undefined);
          await api.call("authLogout");
        } finally {
          if (native) await native.auth.clear().catch(() => undefined);
          // 先把 me 置空（仍挂着的 observer 立即拿到 null），再清掉其他缓存。
          // 不能 qc.clear()：已挂载的 useQuery observer 仍指向被移除的旧 query，会继续显示已登录（退出后跳回首页）。
          qc.setQueryData(ME_KEY, null);
          qc.removeQueries({ predicate: (q) => q.queryKey[0] !== ME_KEY[0] });
        }
      },
      async updateMe(patch) {
        if (!user) return;
        apply({ ...user, ...patch } as User);
        apply(await api.call("mePatch", { body: patch }));
      },
    }),
    [me.isPending, user, api, qc, apply, native],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionApi {
  const s = useContext(Ctx);
  if (!s) throw new Error("SessionProvider missing");
  return s;
}

export function useUser(): User {
  const s = useSession().state;
  if (s.status !== "authed") throw new Error("not authed");
  return s.user;
}

/** 切换语言：界面立即切换；已登录时同步到用户设置 */
export function useSetLocale() {
  const { i18n } = useT();
  const session = useSession();
  return async (l: Locale) => {
    await i18n.changeLanguage(l);
    if (session.state.status === "authed") await session.updateMe({ locale: l }).catch(() => {});
    else markExplicitLocale(l);
  };
}

const EXPLICIT_KEY = "as.locale.explicit";
function markExplicitLocale(l: Locale) {
  try {
    sessionStorage.setItem(EXPLICIT_KEY, l);
  } catch {
    /* ignore */
  }
}
function readExplicitLocale(): Locale | null {
  try {
    const v = sessionStorage.getItem(EXPLICIT_KEY);
    return v && (SUPPORTED_LOCALES as readonly string[]).includes(v) ? (v as Locale) : null;
  } catch {
    return null;
  }
}
function clearExplicitLocale() {
  try {
    sessionStorage.removeItem(EXPLICIT_KEY);
  } catch {
    /* ignore */
  }
}
